import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Collapse,
  IconButton,
  Divider,
  Avatar,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useSelector } from 'react-redux';
import { getFeedbackApi } from '../../api';
import Paginations from '../../common/pagination/Paginations';
import Loader from '../../common/loader/Loader';
import configuration from '../../config/Config';
import { formatCount } from '../../utils/formatCount';

const EMOJI_MAP = {
  frustrated: { icon: SentimentVeryDissatisfiedIcon, label: 'Frustrated', color: '#EA001B', bg: '#fff0f0' },
  happy:      { icon: SentimentSatisfiedAltIcon,     label: 'Happy',      color: '#065F46', bg: '#e8f5f0' },
  sad:        { icon: SentimentDissatisfiedIcon,     label: 'Sad',        color: '#f59e0b', bg: '#fffbeb' },
};

const FeedbackItem = ({ item }) => {
  const [expanded, setExpanded] = useState(false);

  const fullname    = item?.user?.fullname || 'Anonymous';
  const email       = item?.user?.email    || '';
  const rawPicture  = item?.user?.picture  || '';
  const picture     = rawPicture ? `${configuration.cdnFront}/${rawPicture}` : '';
  const description = item?.description   || '';
  const emojiData   = EMOJI_MAP[item?.emoji?.toLowerCase()] || null;

  const date = item?.createdAt
    ? new Date(item.createdAt).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    : '';

  const EmojiIcon = emojiData?.icon;

  return (
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        mb: 1.5,
        overflow: 'hidden',
        background: '#fff',
        boxShadow: '0px 1px 4px rgba(0,0,0,0.06)',
      }}
    >
      {/* Row header */}
      <Box
        onClick={() => setExpanded((p) => !p)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          '&:hover': { background: '#f9f9f9' },
        }}
      >
        {/* Left: avatar + name / email / date */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Avatar
            src={picture}
            alt={fullname}
            sx={{
              width: 40,
              height: 40,
              background: 'linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)',
              fontSize: '15px',
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {fullname.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#362B24', fontFamily: 'Heebo' }}>
              {fullname}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.2 }}>
              {email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <EmailOutlinedIcon sx={{ fontSize: 12, color: '#8B8B8B' }} />
                  <Typography sx={{ fontSize: '12px', color: '#8B8B8B', fontFamily: 'Heebo' }}>
                    {email}
                  </Typography>
                </Box>
              )}
              {date && (
                <Typography sx={{ fontSize: '12px', color: '#bbb', fontFamily: 'Heebo' }}>
                  {email ? '·' : ''} {date}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Right: sentiment chip + chevron */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1 }}>
          {emojiData && EmojiIcon && (
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmojiIcon sx={{ fontSize: 16, color: emojiData.color }} />
                  <span>{emojiData.label}</span>
                </Box>
              }
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{
                backgroundColor: emojiData.bg,
                color: emojiData.color,
                fontFamily: 'Heebo',
                fontSize: '12px',
                fontWeight: 500,
                border: 'none',
                height: 26,
                '& .MuiChip-label': { px: 1.5, display: 'flex', alignItems: 'center' },
              }}
            />
          )}
          <IconButton
            size="small"
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
              color: '#065F46',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Expanded body */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ px: 2.5, py: 2 }}>
          {description ? (
            <Typography sx={{ fontSize: '14px', color: '#555', lineHeight: 1.7, fontFamily: 'Heebo' }}>
              {description}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: '14px', color: '#aaa', fontStyle: 'italic', fontFamily: 'Heebo' }}>
              No description provided.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const Feedbacks = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFeedback, setTotalFeedback] = useState(0);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const response = await getFeedbackApi({ token, currentPage });
      setFeedbacks(response?.data || []);
      setTotalPages(response?.totalPages || 1);
      setTotalFeedback(response?.totalFeedback || 0);
    } catch (error) {
      console.log('Feedback fetch error', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, [currentPage]);

  return (
    <Card sx={{ padding: '20px', borderRadius: '25px', marginBottom: '10px', backgroundColor: '#fff' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#362B24', fontFamily: 'Heebo' }}>
          All Feedbacks
        </Typography>
        {!loading && totalFeedback > 0 && (
          <Typography sx={{ fontSize: '13px', color: '#888', fontFamily: 'Heebo' }}>
            {formatCount(totalFeedback)} total
          </Typography>
        )}
      </Box>

      {loading ? (
        <Loader variant="ticket" />
      ) : feedbacks.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography sx={{ color: '#aaa', fontSize: '15px', fontFamily: 'Heebo' }}>No feedbacks found.</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            maxHeight: '60vh',
            overflowY: 'auto',
            pr: 0.5,
            '&::-webkit-scrollbar': { width: '5px' },
            '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb': { background: '#0D7B5C', borderRadius: '4px' },
          }}
        >
          {feedbacks.map((item, index) => (
            <FeedbackItem key={item?._id || index} item={item} />
          ))}
        </Box>
      )}

      {!loading && totalPages > 1 && (
        <Paginations
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          disabled={loading}
        />
      )}
    </Card>
  );
};

export default Feedbacks;
